/**
 * The embed contract with the appLockr platform.
 *
 * The platform's `EmbeddedSupersetReport` / `EmbeddedContentRenderer` do not
 * know which backend answers a report — they POST the embed bridge and render
 * whatever comes back in an iframe. Filters travel one way, as URL parameters,
 * exactly as they did with Superset. So this file's job is to decide what on
 * the embed URL is a query parameter and what is ours.
 *
 * The rule is a denylist, not an allowlist. Superset dashboards read arbitrary
 * parameters through `url_param(...)`, and the platform is free to add more
 * (a date range, a store, a genre) without this app shipping a change. An
 * allowlist would silently drop each new one and the report would quietly show
 * the wrong slice — the worst kind of failure. Anything not reserved below is
 * forwarded to the query.
 */

/** Parameters this app consumes itself and must not forward upstream. */
const RESERVED = new Set([
  'rt', // session token from the handoff — arrives in the fragment, never the query
  'standalone', // Superset chrome flag the platform still sends
  'theme', // light/dark hint; handled by color-scheme
  'native_filters_key', // Superset filter-state key, meaningless here
  // Partner scope is identity: it rides the signed handoff token, never the URL.
  // The API allow-lists it out of anything a caller supplies, so this is
  // belt-and-braces — but it keeps the forwarder honest to the invariant that a
  // caller cannot ask for another partner's data by putting an id on the URL.
  'user_partner_id',
  'user_partner_ids',
]);

/** Names the platform is documented to send. Used for display, not filtering. */
export const KNOWN_PARAMS = [
  'app_id',
  'app_ids',
  'user_partner_id',
  'platform',
  'category',
  'country',
  'date_from',
  'date_to',
] as const;

/** Upstream query strings have to stay sane; a pathological value is dropped. */
const MAX_VALUE_LENGTH = 4096;

/**
 * When a standalone visit to `/d/<slug>` or `/w/<id>` has no app selection,
 * the seeded base charts still have something to render against. This is what
 * a widget preview reached without going through the platform's app picker,
 * or an author opening a dashboard in a new tab, defaults to.
 *
 * A real embed from the platform always carries `app_id` / `app_ids`, which
 * overrides this. **The default is scoped to non-embed contexts** — see
 * `isPlatformEmbed` — so a misconfigured platform embed shows an honest empty
 * state ("Select an app…") rather than silently rendering Angry Birds data as
 * if it were the viewer's selection.
 */
export const DEFAULT_APP_ID = 'com.rovio.angrybirdsfriends';

/**
 * True when this document is running inside a cross-origin iframe — the only
 * context a real platform embed runs in. If the parent is same-origin (Vite
 * dev's own iframe preview, an author opening the app in a preview harness)
 * or absent, this is standalone and the default preview app should apply.
 *
 * `window.top` throws when the parent is cross-origin, and that throw is the
 * signal: no need to compare a value we can't read.
 */
export const isPlatformEmbed = (self: Window = window): boolean => {
  try {
    return self.top !== self && self.top !== null;
  } catch {
    // Cross-origin `top` access throws — that only happens inside a real embed.
    return true;
  }
};

/**
 * The platform joins a multi-app selection with PIPES, not commas —
 * `createSupersetReportUrl` in chimera-web builds `app_ids` as
 * `map(metadata, 'bundleId').join('|')`. The value is forwarded upstream
 * verbatim, because that is exactly the string the Superset datasets were
 * written to split; only code that needs to count or list the apps has to know
 * the separator. Commas are accepted too, so a hand-built URL still works.
 */
const APP_ID_SEPARATOR = /[|,]/;

/**
 * Everything on the current URL that should travel to the query, normalised.
 *
 * Repeated keys (`?app_ids=1&app_ids=2`) collapse to one comma-joined value,
 * which is the form the chart API expects and the form Superset produced.
 *
 * `embedded` is the second half of the fallback rule: outside a platform embed
 * we inject the default preview app so a fresh install renders; inside one we
 * do not, so a misconfigured content item shows an honest empty state instead
 * of silently substituting our preview app. Callers only pass an override for
 * testing — production reads `isPlatformEmbed()` from `window`.
 */
export const embedParams = (
  search = window.location.search,
  embedded = isPlatformEmbed(),
): Record<string, string> => {
  const params: Record<string, string> = {};
  const query = new URLSearchParams(search);

  for (const key of new Set(query.keys())) {
    if (RESERVED.has(key)) continue;

    const values = query
      .getAll(key)
      .map((v) => v.trim())
      .filter(Boolean);

    // Repeated keys (`?app_ids=a&app_ids=b`) collapse to the pipe-joined form
    // the platform itself produces, so both spellings reach the query the same.
    const value = values.join('|');

    if (value && value.length <= MAX_VALUE_LENGTH) params[key] = value;
  }

  // Fall back to the default preview app so the seeded base charts and a fresh
  // widget URL are not stuck on an empty state when no platform picker is in
  // front of them. Deliberately NOT applied inside a real platform embed: if
  // the platform ever ships a content item without `app_id`, an empty state
  // is a truthful signal, whereas silently rendering the preview app would
  // read to the viewer as their own selection.
  if (!embedded && !params.app_id && !params.app_ids) {
    params.app_id = DEFAULT_APP_ID;
  }

  return params;
};

/** True when the app filter the dashboard needs is actually present. */
export const hasAppSelection = (params: Record<string, string>): boolean =>
  Boolean(params.app_id || params.app_ids);

/** The apps the platform selected, as bundle ids. */
export const selectedApps = (params: Record<string, string>): string[] => {
  if (params.app_ids) return params.app_ids.split(APP_ID_SEPARATOR).filter(Boolean);
  return params.app_id ? [params.app_id] : [];
};

/** How many apps the platform selected — drives "app" vs "3 apps" in the UI. */
export const appCount = (params: Record<string, string>): number =>
  selectedApps(params).length;
